/** Per-site detail: status, serving PoP, uptime, hosts, and why a site is degraded. */

import { Icon, TriangleAlert } from "@norican/icons";
import { Badge, DataGrid, EmptyState, Tooltip } from "@norican/react";

import type { NetworkSite } from "@/lib/api";

import { PanelCard } from "./panel-card";
import {
  formatDuration,
  formatHosts,
  formatTimestamp,
  hostsTone,
  STATE_LABEL,
  STATE_TONE,
} from "./status";

interface SiteTableProps {
  /** Every site Cato knows about. */
  sites: NetworkSite[];
}

export function SiteTable({ sites }: SiteTableProps) {
  return (
    <PanelCard
      title="Site status"
      caption={`${sites.length} ${sites.length === 1 ? "site" : "sites"}`}
      padding="none"
    >
      {sites.length === 0 ? (
        <div className="p-6">
          <EmptyState
            size="sm"
            title="No sites reported"
            description="Cato returned an empty estate for this account."
          />
        </div>
      ) : (
        <div className="h-[28rem]">
          {/* DataGrid's scroller is `max-height: 100%`, so pinned columns and the
              sticky header only get a scroll region inside a DEFINITE height.
              Without one they latch onto the page viewport and drift while the
              page scrolls. Every DataGrid story wraps the grid this way. */}
          <DataGrid<NetworkSite>
            aria-label="Cato sites"
            data={sites}
            getRowId={(site) => site.id}
            getRowSearchText={(site) => `${site.name} ${site.pop_name ?? ""} ${site.country ?? ""}`}
            density="comfortable"
            columns={[
              {
                id: "name",
                header: "Site",
                accessor: (site) => site.name,
                sortable: true,
                pinned: "left",
                cell: (site) => (
                  <div className="flex flex-col">
                    <span className="text-fg font-medium">{site.name}</span>
                    <span className="text-fg-muted text-xs">
                      {[site.city, site.country].filter(Boolean).join(", ") ||
                        site.site_type ||
                        "—"}
                    </span>
                  </div>
                ),
              },
              {
                id: "status",
                header: "Status",
                accessor: (site) => site.connectivity,
                sortable: true,
                cell: (site) => (
                  <div className="flex items-center gap-2">
                    <Badge tone={STATE_TONE[site.connectivity]} variant="subtle" size="sm">
                      {STATE_LABEL[site.connectivity]}
                    </Badge>
                    {(site.degraded_reasons?.length ?? 0) > 0 && (
                      <Tooltip.Root>
                        <Tooltip.Trigger
                          className="text-warning-fg inline-flex"
                          aria-label={`Why ${site.name} is degraded`}
                        >
                          <Icon icon={TriangleAlert} size="sm" />
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                          <Tooltip.Positioner side="top">
                            <Tooltip.Popup>
                              <ul className="flex list-disc flex-col gap-1 pl-4 text-xs">
                                {site.degraded_reasons?.map((reason) => (
                                  <li key={reason}>{reason}</li>
                                ))}
                              </ul>
                            </Tooltip.Popup>
                          </Tooltip.Positioner>
                        </Tooltip.Portal>
                      </Tooltip.Root>
                    )}
                  </div>
                ),
              },
              {
                id: "pop",
                header: "PoP",
                accessor: (site) => site.pop_name ?? null,
                sortable: true,
              },
              {
                id: "uptime",
                header: "Connected for",
                align: "end",
                numeric: true,
                accessor: (site) => site.uptime_seconds ?? null,
                sortable: true,
                cell: (site) => (
                  <span className="text-fg tabular-nums">
                    {formatDuration(site.uptime_seconds)}
                  </span>
                ),
              },
              {
                // Count against the licensed limit, not on its own: a site at
                // its host limit stops serving new devices, and that is
                // reported as "the wifi is broken" rather than as a licence
                // problem. The limit comes off `accountMetrics`, so it can be
                // unread while the count is known.
                id: "hosts",
                header: "Hosts",
                align: "end",
                numeric: true,
                accessor: (site) => site.host_count ?? null,
                sortable: true,
                cell: (site) => (
                  <Badge
                    tone={hostsTone(site.host_count, site.metrics?.host_limit)}
                    variant="subtle"
                    size="sm"
                  >
                    {formatHosts(site.host_count, site.metrics?.host_limit)}
                  </Badge>
                ),
              },
              {
                id: "sockets",
                header: "Sockets",
                align: "end",
                numeric: true,
                accessor: (site) => site.devices?.length ?? 0,
                cell: (site) => {
                  const devices = site.devices ?? [];
                  const up = devices.filter((device) => device.connected !== false).length;
                  return (
                    <span className="text-fg tabular-nums">
                      {up} / {devices.length}
                    </span>
                  );
                },
              },
              {
                id: "last-connected",
                header: "Last connected",
                accessor: (site) => site.last_connected ?? null,
                sortable: true,
                cell: (site) => (
                  <span className="text-fg-muted text-xs">
                    {formatTimestamp(site.last_connected)}
                  </span>
                ),
              },
            ]}
          />
        </div>
      )}
    </PanelCard>
  );
}
